import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useWebsiteContent } from '../hooks/useWebsiteContent';
import { WebsitePreview, type PublicPage } from './WebsitePreview';
import { BrandLogo } from './BrandLogo';
import {
  getInitialLocale,
  getLocalizedValue,
  persistLocale,
  updateUrlLocale,
  type SupportedLocale,
} from '../lib/localization';
import {
  absoluteUrl,
  applyRobots,
  DEFAULT_TITLE,
  hreflangUrl,
  organizationSchema,
  pageCanonicalUrl,
  setJsonLd,
  setLinkRel,
  setMetaByName,
  setMetaByProperty,
  siteOrigin,
} from '../lib/seo';
import { isStaleWebsiteCopy, localizedText, visibleBlogPosts, WEBSITE_DEFAULTS } from '../lib/websiteDefaults';
import { visibleSolutions } from '../lib/solutions';

/**
 * Public Website Component
 * Displays the public-facing website content without authentication
 * The main site: / (home), /contact, /blog, /blog/:slug, /solutions and /solutions/:slug.
 * The original Framer site is proxied by the Worker at /site as a reference.
 */
export function PublicWebsite({ page: pageType = 'home' }: { page?: PublicPage['type'] } = {}) {
  const { slug = '' } = useParams<{ slug: string }>();
  const page: PublicPage =
    pageType === 'post' || pageType === 'solution' ? { type: pageType, slug } : { type: pageType };
  const { content, loading, error, reloadContent } = useWebsiteContent();
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastRefreshRef = useRef<number>(0);
  const isRefreshingRef = useRef<boolean>(false);
  const [locale, setLocale] = useState<SupportedLocale>(() => getInitialLocale());
  const tr = (en: string, es: string) => (locale === 'es' ? es : en);

  // Auto-refresh content periodically (every 60 seconds)
  useEffect(() => {
    // Don't reload on initial mount - useWebsiteContent already loads
    // Set up periodic refresh with debounce
    refreshIntervalRef.current = setInterval(() => {
      const now = Date.now();
      // Only refresh if it's been at least 60 seconds since last refresh and not currently refreshing
      if (now - lastRefreshRef.current >= 60000 && !isRefreshingRef.current) {
        isRefreshingRef.current = true;
        reloadContent().finally(() => {
          lastRefreshRef.current = Date.now();
          isRefreshingRef.current = false;
        });
      }
    }, 60000); // Check every 60 seconds

    // Refresh when window regains focus (with debounce)
    const handleFocus = () => {
      const now = Date.now();
      // Only refresh if it's been at least 10 seconds since last refresh and not currently refreshing
      if (now - lastRefreshRef.current >= 10000 && !isRefreshingRef.current) {
        isRefreshingRef.current = true;
        reloadContent().finally(() => {
          lastRefreshRef.current = Date.now();
          isRefreshingRef.current = false;
        });
      }
    };
    window.addEventListener('focus', handleFocus);

    // Cleanup
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      window.removeEventListener('focus', handleFocus);
    };
  }, [reloadContent]);

  useEffect(() => {
    updateUrlLocale(locale);
    persistLocale(locale);
    document.documentElement.lang = locale;
  }, [locale]);

  // Override static SEO from index.html when CMS content is available.
  // Google Analytics is loaded from index.html — do not inject a second gtag.
  useEffect(() => {
    const seo = content?.seo?.content || {};
    const globalSeo = content?.global?.seo_metadata || {};
    const localizedSeo = getLocalizedValue<Record<string, any>>(seo, locale) || seo;
    const localizedGlobalSeo = getLocalizedValue<Record<string, any>>(globalSeo, locale) || globalSeo;
    const origin = siteOrigin(localizedSeo.baseUrl || localizedGlobalSeo.baseUrl);
    const title = localizedSeo.metaTitle || localizedGlobalSeo.metaTitle || WEBSITE_DEFAULTS.seo.metaTitle;
    const metaDescription =
      localizedSeo.metaDescription ||
      localizedGlobalSeo.metaDescription ||
      localizedText(WEBSITE_DEFAULTS.seo.metaDescription, locale);
    const keywords = localizedSeo.keywords || localizedGlobalSeo.keywords;
    const robotsIndex =
      localizedSeo.robotsIndex !== undefined
        ? localizedSeo.robotsIndex
        : localizedGlobalSeo.robotsIndex !== undefined
          ? localizedGlobalSeo.robotsIndex
          : true;
    const robotsFollow =
      localizedSeo.robotsFollow !== undefined
        ? localizedSeo.robotsFollow
        : localizedGlobalSeo.robotsFollow !== undefined
          ? localizedGlobalSeo.robotsFollow
          : true;
    const canonicalUrl = pageCanonicalUrl(
      origin,
      window.location.pathname,
      locale,
      localizedSeo.canonicalUrl || localizedGlobalSeo.canonicalUrl
    );
    const ogTitle = localizedGlobalSeo.ogTitle || title;
    const ogDescription = localizedGlobalSeo.ogDescription || metaDescription;
    const headerLogo =
      typeof content?.header?.content?.logo === "string" ? content.header.content.logo : undefined;

    // Blog pages: the post (or blog list) supplies its own title and description.
    const blogSection = isStaleWebsiteCopy(content) ? undefined : content?.blog?.content;
    const contactSection = isStaleWebsiteCopy(content) ? undefined : content?.contact?.content;
    const blogPost =
      page.type === 'post'
        ? visibleBlogPosts(blogSection?.items).find((item) => item.slug === page.slug)
        : undefined;
    const solutionsSection = isStaleWebsiteCopy(content) ? undefined : content?.solutions?.content;
    const solution =
      page.type === 'solution'
        ? visibleSolutions(solutionsSection?.items).find((item) => item.slug === page.slug)
        : undefined;
    const pageTitle = solution
      ? `${solution.name} | ON45D Studio`
      : page.type === 'solutions'
        ? `${locale === 'es' ? 'Soluciones' : 'Solutions'} | ${title}`
        : blogPost
      ? `${localizedText(blogPost.title, locale)} | ON45D Studio`
      : page.type === 'blog'
        ? `Blog | ${title}`
        : page.type === 'contact'
          ? `${localizedText(contactSection?.pageTitle ?? WEBSITE_DEFAULTS.contact.pageTitle, locale)} | ${title}`
          : title;
    const pageDescription = solution
      ? localizedText(solution.description, locale) || localizedText(solution.summary, locale) || metaDescription
      : page.type === 'solutions'
        ? localizedText(solutionsSection?.pageDescription ?? WEBSITE_DEFAULTS.solutions.pageDescription, locale) || metaDescription
        : blogPost
      ? localizedText(blogPost.excerpt, locale) || metaDescription
      : page.type === 'blog'
        ? localizedText(blogSection?.pageDescription ?? WEBSITE_DEFAULTS.blog.pageDescription, locale) || metaDescription
        : metaDescription;

    if (pageTitle) document.title = pageTitle;
    if (pageDescription) setMetaByName("description", pageDescription);
    if (keywords) setMetaByName("keywords", keywords);
    applyRobots(Boolean(robotsIndex), Boolean(robotsFollow));
    setLinkRel("canonical", canonicalUrl);
    setLinkRel("alternate", hreflangUrl(origin, window.location.pathname, "es"), { hreflang: "es" });
    setLinkRel("alternate", hreflangUrl(origin, window.location.pathname, "en"), { hreflang: "en" });
    setLinkRel("alternate", hreflangUrl(origin, window.location.pathname, "en"), {
      hreflang: "x-default",
    });
    const isBlogPage = page.type !== 'home';
    if (ogTitle) setMetaByProperty("og:title", isBlogPage ? pageTitle : ogTitle);
    if (ogDescription) setMetaByProperty("og:description", isBlogPage ? pageDescription : ogDescription);
    setMetaByProperty("og:url", canonicalUrl);
    if (localizedGlobalSeo.ogImage) {
      setMetaByProperty("og:image", absoluteUrl(localizedGlobalSeo.ogImage, origin));
    }
    setMetaByName("twitter:title", ogTitle || DEFAULT_TITLE);
    if (ogDescription) setMetaByName("twitter:description", ogDescription);

    const schemaType =
      localizedGlobalSeo.schemaEnabled && localizedGlobalSeo.schemaType
        ? localizedGlobalSeo.schemaType
        : "ProfessionalService";
    setJsonLd(
      organizationSchema({
        name: title || DEFAULT_TITLE,
        url: origin,
        type: schemaType,
        logo: headerLogo ? absoluteUrl(headerLogo, origin) : undefined,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, locale, page.type, slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <div className="text-center">
          <BrandLogo className="mx-auto h-10 w-auto" />
          <p className="mt-3 text-sm text-paper/50">{tr('Loading website...', 'Cargando sitio web...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <div className="text-center">
          <p className="text-red-300">{tr('Error loading website content', 'Error al cargar el contenido del sitio')}</p>
          <p className="mt-2 text-sm text-paper/50">{error}</p>
        </div>
      </div>
    );
  }

  // Convert content to the format expected by WebsitePreview
  const previewData = content ? Object.keys(content).reduce((acc, key) => {
    acc[key] = { content: content[key].content || {} };
    return acc;
  }, {} as Record<string, any>) : {};

  return (
    <div className="relative">
      <WebsitePreview previewData={previewData} locale={locale} onLocaleChange={setLocale} page={page} />
    </div>
  );
}

